/**
 * DVL Gallery Filter (Galleria / Swiper)
 * ======================================
 * Per-variant + "collection" image filtering for Galleria's Swiper galleries.
 *
 * Galleria never re-renders the gallery on variant change (variant-description.js
 * only swaps the description), so this filters the existing Swiper slides
 * client-side, using Swiper's loop-safe removeAllSlides()/appendSlide() API — no
 * Swiper config is replicated and theme.js is left untouched.
 *
 * Rules (mirror the Dawn Liquid snippets):
 *   - "collection" in the filename  -> always hidden (if hide_collection on)
 *   - variant-specific image (filename carries a full variant SKU, OR it is a
 *     variant's featured image) -> shown only for the exact selected variant
 *   - body/non-variant image -> matched by COLOUR (filename contains the selected
 *     variant's Color option value)
 *   - otherwise -> shown (shared)
 *
 * Data it relies on (both already present / cheap to add):
 *   - #ProductJson-<sectionId>  : full product JSON (variants, options, featured_image)
 *   - each .swiper-slide has     : id="<image.id>" and data-dvl-file="<filename>"
 *   - #product-gallery flags     : data-dvl-filter-variant / data-dvl-hide-collection
 *
 * Gated by the two flags; inert unless enabled in the section settings.
 */
(function () {
  'use strict';

  var STORE = new WeakMap(); // swiper container el -> [full slide outerHTML]

  function norm(s) { return (s || '').toString().toLowerCase(); }

  function readFlags() {
    var host = document.getElementById('product-gallery') ||
               document.querySelector('[id^="product-gallery"]');
    if (!host) return { on: false };
    var fv = host.getAttribute('data-dvl-filter-variant');
    var hc = host.getAttribute('data-dvl-hide-collection');
    return {
      host: host,
      filterVariant: fv === 'true',
      // default ON: hide collection unless explicitly "false"
      hideCollection: hc !== 'false',
      on: fv === 'true' || hc !== 'false'
    };
  }

  function getProduct() {
    var el = document.querySelector('[id^="ProductJson-"]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function colorIndex(product) {
    for (var i = 0; i < (product.options || []).length; i++) {
      if (/^colou?r$/i.test(product.options[i])) return i;
    }
    return -1;
  }

  function buildContext(product) {
    var ci = colorIndex(product);
    var skus = product.variants.map(function (v) { return norm(v.sku); }).filter(Boolean);
    var colorValues = [];
    if (ci >= 0) {
      product.variants.forEach(function (v) {
        var c = norm(v.options && v.options[ci]);
        if (c && colorValues.indexOf(c) < 0) colorValues.push(c);
      });
    }
    var featuredOwner = {}; // imageId -> owning variant sku
    product.variants.forEach(function (v) {
      if (v.featured_image && v.featured_image.id) featuredOwner[v.featured_image.id] = norm(v.sku);
    });
    return { ci: ci, skus: skus, colorValues: colorValues, featuredOwner: featuredOwner, variants: product.variants };
  }

  /* A variant with NO images of its own (e.g. a port length added before it was photographed) would
     otherwise leave the gallery empty. Don't fall back to EVERYTHING, which would show every variant's
     shots plus the hidden "collection" composites. Borrow a sibling's set instead: same colour first,
     in product order, so Black / Z Long shows the Black / Long images. */
  function standIns(ctx, variant) {
    var colour = function (v) { return ctx.ci >= 0 ? norm(v.options && v.options[ctx.ci]) : ''; };
    var mine = colour(variant);
    var others = (ctx.variants || []).filter(function (v) { return v.id !== variant.id; });
    return others.filter(function (v) { return colour(v) === mine; })
      .concat(others.filter(function (v) { return colour(v) !== mine; }));
  }

  function isCollectionFile(file) { return norm(file).indexOf('collection') >= 0; }

  function currentVariant(product) {
    // The FORM is the live selection; `?variant=` is only how the page was entered. Reading the URL
    // first pinned the gallery to the entry variant on any theme that updates the selectors without
    // rewriting the URL — pick another option and the images never changed again.
    var input = document.querySelector('select[name="id"], input[name="id"]:checked, input[name="id"][type="hidden"]');
    var vid = input && input.value ? input.value : null;
    if (!vid) vid = new URLSearchParams(location.search).get('variant');
    var v = null;
    if (vid) v = product.variants.filter(function (x) { return String(x.id) === String(vid); })[0];
    if (!v) v = product.variants.filter(function (x) { return x.available; })[0] || product.variants[0];
    return v;
  }

  function ownerOf(file, slideId, ctx) {
    for (var i = 0; i < ctx.skus.length; i++) {
      var s = ctx.skus[i];
      if (file.indexOf(s + '-') >= 0 || file.indexOf(s + '.') >= 0 || file.indexOf(s + '_') >= 0) return s;
    }
    if (slideId && ctx.featuredOwner[slideId]) return ctx.featuredOwner[slideId];
    return '';
  }

  function imgColorOf(file, ctx) {
    var found = '';
    ctx.colorValues.forEach(function (c) { if (c && file.indexOf(c) >= 0) found = c; });
    return found;
  }

  function isVisible(slide, ctx, variant, flags) {
    var file = norm(slide.getAttribute('data-dvl-file'));
    if (!file) return true; // can't classify -> keep
    if (flags.hideCollection && file.indexOf('collection') >= 0) return false;
    if (!flags.filterVariant) return true;
    var owner = ownerOf(file, slide.getAttribute('id'), ctx);
    var curSku = norm(variant.sku);
    if (owner) return owner === curSku;
    var col = imgColorOf(file, ctx);
    var curColor = ctx.ci >= 0 ? norm(variant.options && variant.options[ctx.ci]) : '';
    if (col) return col === curColor;
    return true;
  }

  // Snapshot the real (non-clone) slides' HTML the first time we touch a swiper,
  // so we can rebuild any subset later even after loop-mode has added clones.
  function captureFull(container) {
    if (STORE.has(container)) return STORE.get(container);
    var reals = Array.prototype.slice.call(
      container.querySelectorAll('.swiper-wrapper > .swiper-slide:not(.swiper-slide-duplicate)')
    );
    var full = reals.map(function (s) {
      var c = s.cloneNode(true);
      c.className = c.className
        .replace(/swiper-slide-[a-z-]+/g, '')
        .replace(/\s{2,}/g, ' ').trim();
      c.style.width = '';
      return c.outerHTML;
    });
    STORE.set(container, full);
    return full;
  }

  function filterSwiperContainer(container, ctx, variant, flags) {
    if (!container) return;
    var sw = container.swiper;
    if (!sw) return;
    var full = captureFull(container);
    if (!full.length) return;

    var tmp = document.createElement('div');
    function pick(v) {
      return full.filter(function (html) {
        tmp.innerHTML = html;
        return isVisible(tmp.firstElementChild, ctx, v, flags);
      });
    }
    var keep = pick(variant);
    var subs = standIns(ctx, variant);
    for (var si = 0; !keep.length && si < subs.length; si++) keep = pick(subs[si]);
    // never blank the gallery — but still never show the collection composites
    if (!keep.length) keep = full.filter(function (html) {
      tmp.innerHTML = html;
      return !(flags.hideCollection && isCollectionFile(tmp.firstElementChild.getAttribute('data-dvl-file')));
    });
    if (!keep.length) keep = full.slice();

    try {
      sw.removeAllSlides();
      sw.appendSlide(keep);
      sw.update();
      if (sw.slideTo) sw.slideTo(sw.params && sw.params.loop ? (sw.loopedSlides || 0) : 0, 0);
    } catch (e) { /* leave gallery as-is on any Swiper hiccup */ }
  }

  function galleryContainers(sectionId) {
    var sels = [
      '.swiper-main-' + sectionId,
      '#swiper-gallery-' + sectionId,
      '#swiper-gallery-vert-' + sectionId,
      '#swiper-gallery-thumbs-' + sectionId,
      '#swiper-gallery-thumbs-vert-' + sectionId
    ];
    var out = [];
    sels.forEach(function (s) {
      var el = document.querySelector(s);
      if (el) out.push(el);
    });
    return out;
  }

  function run() {
    var flags = readFlags();
    if (!flags.on) return;
    var product = getProduct();
    if (!product || !product.variants) return;

    var host = flags.host;
    var sectionId = (host.className.match(/product-gallery-([\w-]+)/) || [])[1] ||
      (document.querySelector('[id^="ProductJson-"]').id.replace('ProductJson-', ''));

    var ctx = buildContext(product);

    function apply() {
      var variant = currentVariant(product);
      if (!variant) return;
      galleryContainers(sectionId).forEach(function (c) {
        filterSwiperContainer(c, ctx, variant, flags);
      });
    }

    // Wait for Swiper to be initialised on the main container, then filter.
    var tries = 0;
    (function waitInit() {
      var mains = galleryContainers(sectionId);
      var ready = mains.length && mains.every(function (c) { return c.swiper; });
      if (ready) { apply(); }
      else if (tries++ < 60) { setTimeout(waitInit, 100); }
    })();

    // Re-filter on variant change: watch the variant id + the same events
    // variant-description.js listens to. Debounced.
    var lastVid = null;
    function currentVid() {
      // Form first: `?variant=` is only the entry point, and a theme that updates the selectors
      // without rewriting the URL would otherwise freeze the gallery on the entry variant.
      var input = document.querySelector('select[name="id"], input[name="id"]:checked, input[name="id"][type="hidden"]');
      if (input && input.value) return input.value;
      return new URLSearchParams(location.search).get('variant');
    }
    lastVid = currentVid();
    var t = null;
    function onMaybeChange() {
      clearTimeout(t);
      t = setTimeout(function () {
        var v = currentVid();
        if (v && v !== lastVid) { lastVid = v; apply(); }
      }, 120);
    }
    document.addEventListener('change', function (e) {
      if (e.target.matches && e.target.matches('select[name="id"], input[name="id"], .single-option-selector, [data-single-option-selector]')) onMaybeChange();
    });
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.swatch-element, .color-swatch, .variant-swatch, [data-swatch], .swatch label')) onMaybeChange();
    });
    window.addEventListener('popstate', onMaybeChange);
    ['variant:changed', 'variantChange', 'theme:variant:change'].forEach(function (ev) {
      document.addEventListener(ev, onMaybeChange);
    });
    var _ps = history.pushState, _rs = history.replaceState;
    history.pushState = function () { _ps.apply(this, arguments); setTimeout(onMaybeChange, 10); };
    history.replaceState = function () { _rs.apply(this, arguments); setTimeout(onMaybeChange, 10); };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
